import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Paper
} from '@mui/material';

// Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

// Custom components
import StockSearchBar from '../../../common/StockSearchBar';

const PriceAlertConfig = ({ alertData, onUpdateAlertData }) => {
  const [selectedStock, setSelectedStock] = useState(null);

  // Get the main filters and conditions from alert data
  const mainFilters = useMemo(() => alertData.mainFilters || [], [alertData.mainFilters]);
  const conditions = useMemo(() => alertData.conditions || {}, [alertData.conditions]);

  // Extract the symbol from main filters (should be only one)
  const symbol = mainFilters
    .filter(filter => filter.type === 'symbol')
    .map(filter => filter.value)[0] || '';

  // Set triggerType to 'once' on initial load
  useEffect(() => {
    if (alertData.triggerType !== 'once') {
      onUpdateAlertData({
        ...alertData,
        triggerType: 'once'
      });
    }
  }, [alertData, onUpdateAlertData]);

  // Populate selected stock from existing data on initial load
  useEffect(() => {
    if (symbol && !selectedStock) {
      setSelectedStock(symbol);
    }
  }, [symbol, selectedStock]);

  // Handle selecting a stock - only allow one
  const handleSymbolSelect = useCallback((symbol, resultData) => {

    if (!symbol) return;

    // Clear any existing symbols and set the new one
    const updatedMainFilters = mainFilters
      .filter(filter => filter.type !== 'symbol')
      .concat({ type: 'symbol', value: symbol });

    // Create updated conditions
    let updatedConditions = { ...conditions };

    // Always set this symbol as the condition symbol
    updatedConditions.symbol = symbol;

    // If we have price data, set it
    if (resultData && resultData.price) {
      // For price alerts (above/below)
      if (updatedConditions.direction === 'above' || updatedConditions.direction === 'below' || !updatedConditions.direction) {
        // Default to 'above' if no direction is set
        if (!updatedConditions.direction) {
          updatedConditions.direction = 'above';
        }
        updatedConditions.price = resultData.price.toString();
      }
    }

    // Update the selected stock state
    setSelectedStock(symbol);

    onUpdateAlertData({
      mainFilters: updatedMainFilters,
      conditions: updatedConditions,
      triggerType: 'once' // Always set trigger type to 'once'
    });
  }, [mainFilters, conditions, onUpdateAlertData]);

  // Handle direction change (above/below/up/down)
  const handleDirectionChange = (e) => {
    const direction = e.target.value;

    // Reset price or threshold based on direction
    let updatedConditions = {
      ...conditions,
      direction
    };

    // Remove irrelevant fields based on direction
    if (direction === 'above' || direction === 'below') {
      delete updatedConditions.threshold;
      if (!updatedConditions.price) updatedConditions.price = '';
    } else {
      delete updatedConditions.price;
      if (!updatedConditions.threshold) updatedConditions.threshold = '';
    }

    onUpdateAlertData({
      conditions: updatedConditions
    });
  };

  // Handle price threshold change
  const handlePriceChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        price: e.target.value
      }
    });
  };

  // Handle percentage threshold change
  const handleThresholdChange = (e) => {
    onUpdateAlertData({
      conditions: {
        ...conditions,
        threshold: e.target.value
      }
    });
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        1. Select Stocks to Monitor
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <StockSearchBar
            onSymbolSelect={handleSymbolSelect}
            currentSymbols={symbol ? [symbol] : []}
            maxSymbols={1} // Only allow one stock
            placeholder="Search for a stock by symbol or name"
            disabled={!!symbol} // Disable if a stock is already selected
            {...(symbol ? { hideSearch: true } : {})} // Hide search if stock already selected
          />
        </Box>

        {symbol && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Typography variant="subtitle1">
              Selected: <strong>{symbol}</strong>
            </Typography>
            <Typography
              variant="button"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                // Clear the selected stock
                const updatedMainFilters = mainFilters.filter(filter => filter.type !== 'symbol');
                onUpdateAlertData({
                  mainFilters: updatedMainFilters,
                  conditions: { ...conditions, symbol: '' }
                });
                setSelectedStock(null);
              }}
            >
              Change
            </Typography>
          </Box>
        )}

        {/* Note about one-time alerts */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Price alerts are one-time notifications that trigger once when conditions are met.
        </Typography>
      </Paper>

      {symbol && (
        <>
          <Typography variant="h6" gutterBottom>
            2. Set Alert Conditions
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Alert Condition Type</FormLabel>
              <RadioGroup
                row
                value={conditions.direction || 'above'}
                onChange={handleDirectionChange}
              >
                <FormControlLabel
                  value="above"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingFlatIcon sx={{ color: 'positive.main', mr: 0.5 }} />
                      Above Price
                    </Box>
                  }
                />
                <FormControlLabel
                  value="below"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingFlatIcon sx={{ color: 'negative.main', mr: 0.5 }} />
                      Below Price
                    </Box>
                  }
                />
                <FormControlLabel
                  value="up"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ color: 'positive.main', mr: 0.5 }} />
                      Increase By %
                    </Box>
                  }
                />
                <FormControlLabel
                  value="down"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingDownIcon sx={{ color: 'negative.main', mr: 0.5 }} />
                      Decrease By %
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {(conditions.direction === 'above' || conditions.direction === 'below') && (
              <TextField
                label="Price Threshold"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0 } // Set minimum value to 0
                }}
                fullWidth
                value={conditions.price || ''}
                onChange={(e) => {
                  // Ensure the value is not negative
                  const value = parseFloat(e.target.value);
                  if (value < 0 || isNaN(value)) {
                    // If negative or not a number, don't update or set to 0
                    handlePriceChange({ target: { value: 0 } });
                  } else {
                    handlePriceChange(e);
                  }
                }}
                helperText={`Notify when ${symbol} is ${conditions.direction || 'above'} this price`}
              />
            )}

            {(conditions.direction === 'up' || conditions.direction === 'down') && (
              <TextField
                label="Percentage Change"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0 } // Set minimum value to 0
                }}
                fullWidth
                value={conditions.threshold || ''}
                onChange={(e) => {
                  // Ensure the value is not negative
                  const value = parseFloat(e.target.value);
                  if (value < 0 || isNaN(value)) {
                    // If negative or not a number, don't update or set to 0
                    handleThresholdChange({ target: { value: 0 } });
                  } else {
                    handleThresholdChange(e);
                  }
                }}
                helperText={`Notify when ${symbol} ${conditions.direction === 'up' ? 'increases' : 'decreases'} by this percentage`}
              />
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PriceAlertConfig;